import { gatewayApi as api } from './gatewayApi';

// Interfaces
export interface NCRAction {
  Id?: number;
  Name: string;
  Description?: string;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;
}

export interface NCRActionListResponse {
  data: NCRAction[];
  total: number;
}

export interface NCRActionSearchParams {
  page?: number;
  pageSize?: number;
  search?: string;
  active?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// API Service
export const ncractionApi = {
  // CRUD Operations
  getAll: (params?: NCRActionSearchParams): Promise<NCRActionListResponse> =>
    api.get('/ncraction', params),

  getById: (id: number): Promise<NCRAction> =>
    api.get(`/ncraction/${id}`),

  create: (ncraction: Omit<NCRAction, 'Id'>): Promise<NCRAction> =>
    api.post('/ncraction', ncraction),

  update: (id: number, ncraction: Partial<NCRAction>): Promise<NCRAction> =>
    api.put(`/ncraction/${id}`, ncraction),

  delete: (id: number): Promise<{ success: boolean; message: string }> =>
    api.delete(`/ncraction/${id}`),

  // Search and Filter
  search: (params: NCRActionSearchParams): Promise<NCRActionListResponse> =>
    api.post('/ncraction/search', params),

  // Bulk Operations
  bulkActivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/ncraction/bulk-activate', { ids }),

  bulkDeactivate: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.put('/ncraction/bulk-deactivate', { ids }),

  bulkDelete: (ids: number[]): Promise<{ success: boolean; message: string }> =>
    api.post('/ncraction/bulk-delete', { ids }),

  // Statistics
  getStatistics: (): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> =>
    api.get('/ncraction/statistics'),

  // Export
  exportToCSV: (params?: NCRActionSearchParams): Promise<Blob> =>
    api.getBlob('/ncraction/export', { params }),

  // Validation
  validation: {
    validateNCRAction: (ncraction: Partial<NCRAction>): string[] => {
      const errors: string[] = [];

      // Name validation
      if (!ncraction.Name || ncraction.Name.trim().length === 0) {
        errors.push('Action Name is required');
      } else if (ncraction.Name.trim().length < 2) {
        errors.push('Action Name must be at least 2 characters long');
      } else if (ncraction.Name.trim().length > 100) {
        errors.push('Action Name must be less than 100 characters');
      }

      // Description validation (optional but if provided, validate length)
      if (ncraction.Description && ncraction.Description.trim().length > 500) {
        errors.push('Description must be less than 500 characters');
      }

      // Active validation
      if (ncraction.Active === undefined || ncraction.Active === null) {
        errors.push('Active status is required');
      }

      return errors;
    },

    validateBulkOperation: (ids: number[]): string[] => {
      const errors: string[] = [];

      if (!ids || ids.length === 0) {
        errors.push('At least one NCR Action must be selected');
      }

      if (ids.length > 100) {
        errors.push('Cannot perform bulk operation on more than 100 items at once');
      }

      return errors;
    }
  }
};

export default ncractionApi; 